import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { getAllPosts, getCategories, catSlug } from "@/content/blog";
import Seo from "@/lib/Seo";

export default function BlogArchive() {
  const posts = getAllPosts();
  const categories = useMemo(() => getCategories(), []);

  // Group posts by category (preserving date-desc order from getAllPosts).
  const grouped = useMemo(() => {
    const map = new Map();
    categories.forEach((c) => map.set(c.slug, { name: c.name, slug: c.slug, items: [] }));
    posts.forEach((p) => {
      const s = catSlug(p.category);
      if (!map.has(s)) map.set(s, { name: p.category, slug: s, items: [] });
      map.get(s).items.push(p);
    });
    return Array.from(map.values());
  }, [posts, categories]);

  return (
    <div>
      <Seo
        path="/blog/archive"
        title="Journal Archive — Every Article, Grouped by Topic"
        description="A compact archive of every field note on the Steady Path Journal, grouped by category. Every business funding article on one page."
      />

      {/* Editorial hero */}
      <section className="pt-40 pb-16 border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/60 hover:text-[color:var(--gold)] transition-colors mb-10"
          >
            <ArrowLeft size={14} /> Back to the journal
          </Link>
          <div className="overline">Archive · {posts.length} field notes</div>
          <h1 className="mt-8 font-serif text-6xl md:text-8xl leading-[0.9] tracking-tight max-w-5xl">
            Every article, <em className="italic text-[color:var(--gold)]">on one page</em>.
          </h1>
          <div className="mt-10 h-px w-12 bg-[color:var(--gold)]" />
          <p className="mt-10 max-w-2xl text-[color:var(--bone)]/75 leading-relaxed text-lg">
            For readers who prefer to scan instead of scroll. The full journal
            grouped by category — click any title to read the piece.
          </p>
        </div>
      </section>

      {/* Grouped list */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-x-8 gap-y-16">
            {grouped.map((group) => (
              <motion.div
                key={group.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6 }}
                className="col-span-12 md:col-span-6 border-t border-black/10 pt-8"
                data-testid={`archive-group-${group.slug}`}
              >
                <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
                  <Link
                    to={`/blog/category/${group.slug}`}
                    className="group font-serif text-3xl md:text-4xl leading-tight hover:text-[color:var(--gold)] transition-colors"
                  >
                    {group.name}
                    <ArrowRight
                      size={18}
                      className="inline-block ml-2 -mt-1 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all"
                    />
                  </Link>
                  <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
                    {group.items.length} article{group.items.length === 1 ? "" : "s"}
                  </span>
                </div>
                <ul className="space-y-2">
                  {group.items.map((p) => (
                    <li key={p.slug}>
                      <Link
                        to={`/blog/${p.slug}`}
                        data-testid={`archive-item-${p.slug}`}
                        className="group flex items-baseline justify-between gap-4 py-2 border-b border-black/[0.06] hover:border-[color:var(--gold)]/40 transition-colors"
                      >
                        <span className="font-serif text-lg md:text-xl leading-snug text-[color:var(--bone)] group-hover:text-[color:var(--gold)] transition-colors">
                          {p.title}
                        </span>
                        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--bone)]/45 whitespace-nowrap flex-shrink-0">
                          {p.readTime}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
